import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #priceObserver;
  #lastPrice;
  #priceNotice;
  #priceAnimation;

  connectedCallback() {
    super.connectedCallback();
    this.#lastPrice = this.#readPrice();
    this.#priceNotice = document.createElement('span');
    this.#priceNotice.className = 'cltrs-price-change';
    this.#priceNotice.setAttribute('role', 'status');
    this.#priceNotice.setAttribute('aria-live', 'polite');
    this.#priceNotice.setAttribute('aria-atomic', 'true');
    this.append(this.#priceNotice);
    this.#priceObserver = new MutationObserver(this.#showPriceChange);
    this.#priceObserver.observe(this, {
      childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['data-price-cents', 'data-price-currency'],
    });
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#priceObserver?.disconnect();
    this.#priceAnimation?.cancel();
    this.#priceNotice?.remove();
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }


  #readPrice() {
    const container = this.querySelector('[ref="priceContainer"]');
    if (!container?.hasAttribute('data-price-cents')) return null;
    const amount = Number(container.dataset.priceCents);
    if (!Number.isFinite(amount)) return null;
    return {
      amount,
      currency: container.dataset.priceCurrency,
      text: container.querySelector('.price bdi')?.textContent?.trim() || '',
      container,
    };
  }

  #showPriceChange = () => {
    const next = this.#readPrice();
    const previous = this.#lastPrice;
    this.#lastPrice = next;
    if (!next || !previous || next.currency !== previous.currency || next.amount === previous.amount) return;
    const increased = next.amount > previous.amount;
    const difference = new Intl.NumberFormat(document.documentElement.lang || 'fr', {
      style: 'currency', currency: next.currency,
    }).format(Math.abs(next.amount - previous.amount) / 100);
    this.#priceNotice.textContent = `${increased ? '↑' : '↓'} ${previous.text} → ${next.text} (${increased ? '+' : '−'}${difference})`;
    this.#priceNotice.dataset.direction = increased ? 'up' : 'down';
    if (!this.contains(this.#priceNotice)) this.append(this.#priceNotice);
    this.#priceAnimation?.cancel();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const price = next.container.querySelector('.price');
    this.#priceAnimation = price?.animate([
      { transform: `translateY(${increased ? '8px' : '-8px'})`, opacity: 0.5, backgroundColor: 'rgba(245, 190, 60, 0.3)' },
      { transform: 'translateY(0)', opacity: 1, backgroundColor: 'rgba(245, 190, 60, 0)' },
    ], { duration: 650, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' });
  };

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
